import { TPasskeyListRenderProps } from "@/components/commons/PasskeyManager/PasskeyManager";
import { IconEditSquare, IconTrash } from "@/assets/icons";
import { EllipsisText } from "@/components/elements/EllipsisText";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { background, black, divider, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { Box, Stack, Typography } from "@mui/material";
import { FC } from "react";

const formatYmd = (value: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${y}/${m}/${d}`;
};

const HeadCell: FC<{ label: string; width?: string; flex?: number }> = ({ label, width, flex }) => (
  <Box sx={{ px: "16px", py: "8px", width, flex, flexShrink: 0 }}>
    <Typography variant="body14Bold" color={text.primary}>
      {label}
    </Typography>
  </Box>
);

const BodyCell: FC<{ width?: string; flex?: number; children: React.ReactNode; gap?: string }> = ({ width, flex, children, gap }) => (
  <Stack direction="row" alignItems="center" gap={gap ?? "8px"} sx={{ px: "16px", py: "10px", width, flex, flexShrink: 0, minWidth: 0 }}>
    {children}
  </Stack>
);

export const PasskeyTable: FC<TPasskeyListRenderProps> = ({ passkeys, isLoading, onRename, onDelete, readOnly = false }) => {
  if (isLoading) return <LoadingArea height={120} />;

  if (passkeys.length === 0) {
    return (
      <Typography variant="body14" color={text.secondary}>
        パスキーが登録されていません。ユーザー設定画面の「新規追加」から登録すると、次回からパスワードなしでログインできます。
      </Typography>
    );
  }

  return (
    <Box sx={{ border: `1px solid ${divider.middle}`, borderRadius: rounded.sm, overflow: "hidden" }}>
      <Stack direction="row" sx={{ background: background.primary }}>
        <HeadCell label="ID" width="56px" />
        <HeadCell label="パスキーの名前" flex={1.4} />
        <HeadCell label="追加登録日" width="140px" />
        <HeadCell label="最終使用日" width="140px" />
        <HeadCell label="" width="56px" />
      </Stack>

      {passkeys.map((item, index) => (
        <Stack key={item.id} direction="row" sx={{ background: background.white, borderTop: `1px solid ${divider.middle}` }}>
          <BodyCell width="56px">
            <Typography variant="body14" color={text.primary}>
              {index + 1}
            </Typography>
          </BodyCell>
          <BodyCell flex={1.4} gap="8px">
            <EllipsisText title={item.deviceName || "パスキー"} variant="body14" color={text.primary} maxWidth={240} sx={{ flexShrink: 0 }} />
            <Box
              component="button"
              disabled={readOnly}
              onClick={() => onRename(item)}
              sx={{ display: "flex", flexShrink: 0, p: 0, border: 0, background: "none", cursor: readOnly ? "not-allowed" : "pointer" }}
            >
              <IconEditSquare fontSize={24} color={readOnly ? divider.middle : black[80]} />
            </Box>
          </BodyCell>
          <BodyCell width="140px">
            <Typography variant="body14" color={text.primary}>
              {formatYmd(item.createdAt)}
            </Typography>
          </BodyCell>
          <BodyCell width="140px">
            <Typography variant="body14" color={text.primary}>
              {formatYmd(item.lastUsedAt)}
            </Typography>
          </BodyCell>
          <BodyCell width="56px">
            <Box
              component="button"
              disabled={readOnly}
              onClick={() => onDelete(item)}
              sx={{ display: "flex", p: 0, border: 0, background: "none", cursor: readOnly ? "not-allowed" : "pointer" }}
            >
              <IconTrash fontSize={24} color={readOnly ? divider.middle : text.error_light} />
            </Box>
          </BodyCell>
        </Stack>
      ))}
    </Box>
  );
};
